import { CarouselBreakpoint } from './types';
import { BREAKPOINTS } from '../../utils/breakpoints';
import React from 'react';

/**
 * Get the current breakpoint configuration based on viewport width
 */
export function getCurrentBreakpoint(
  width: number,
  breakpoints: CarouselBreakpoint[]
): CarouselBreakpoint {
  // Find the last breakpoint that matches the current width
  for (let i = breakpoints.length - 1; i >= 0; i--) {
    if (width >= breakpoints[i].width) {
      return breakpoints[i];
    }
  }
  // Fallback to first breakpoint
  return breakpoints[0];
}

/**
 * Calculate the number of visible cards based on viewport width
 */
export function getVisibleCardsCount(width: number, breakpoints: CarouselBreakpoint[]): number {
  return getCurrentBreakpoint(width, breakpoints).visibleCards;
}

/**
 * Check if peek should be shown at current viewport
 */
export function shouldShowPeek(width: number, breakpoints: CarouselBreakpoint[]): boolean {
  return getCurrentBreakpoint(width, breakpoints).showPeek || false;
}

/**
 * Get peek amount percentage at current viewport
 */
export function getPeekAmount(width: number, breakpoints: CarouselBreakpoint[]): number {
  return getCurrentBreakpoint(width, breakpoints).peekAmount || 0;
}

/**
 * Calculate item width including gaps and peek amounts
 */
export function calculateItemWidth(
  containerWidth: number,
  visibleCards: number,
  gap: number = 16,
  showPeek: boolean = false,
  peekAmount: number = 0,
  totalItems?: number
): number {
  if (containerWidth === 0 || visibleCards === 0) return 0;

  // If totalItems is provided and less than visibleCards, adjust calculation
  // to fill the container with the actual number of items
  const actualCardsToShow =
    totalItems !== undefined && totalItems < visibleCards ? totalItems : visibleCards;

  const totalGap = gap * (actualCardsToShow - 1);
  let availableWidth = containerWidth - totalGap;

  if (showPeek) {
    // Reserve space for peek
    const peekWidth = (containerWidth * peekAmount) / 100;
    availableWidth -= peekWidth;
  }

  // Floor the result to prevent sub-pixel issues that can cause layout problems
  // This ensures cards fit perfectly within the container
  return Math.floor(availableWidth / actualCardsToShow);
}

/**
 * Calculate total number of pagination dots for dynamic pagination
 */
export function calculatePaginationDots(totalItems: number, visibleCards: number): number {
  if (totalItems <= visibleCards) return 1;

  return Math.max(1, totalItems - visibleCards + 1);
}

/**
 * Validate and clamp index within bounds
 */
export function validateIndex(
  index: number,
  totalItems: number,
  loop: boolean,
  visibleCards: number
): number {
  if (totalItems === 0) return 0;

  if (loop) {
    // For looping, wrap around
    if (index < 0) return totalItems - 1;
    if (index >= totalItems) return 0;
    return index;
  } else {
    // For non-looping, clamp to valid range
    const maxIndex = Math.max(0, totalItems - visibleCards);
    return Math.max(0, Math.min(index, maxIndex));
  }
}

/**
 * Calculate transform offset for carousel positioning
 */
export function calculateTransformOffset(
  currentIndex: number,
  itemWidth: number,
  gap: number = 16,
  dragOffset: number = 0
): number {
  const baseOffset = -currentIndex * (itemWidth + gap);
  return baseOffset + dragOffset;
}

/**
 * Determine if navigation should be visible based on current state
 */
export function shouldShowNavigation(
  currentIndex: number,
  totalItems: number,
  visibleCards: number,
  loop: boolean,
  direction: 'previous' | 'next'
): boolean {
  if (totalItems <= visibleCards) return false;

  if (loop) return true;

  if (direction === 'previous') {
    return currentIndex > 0;
  } else {
    return currentIndex < totalItems - visibleCards;
  }
}

/**
 * Get responsive thumb size based on viewport width
 */
export function getThumbSize(width: number): number {
  return width >= BREAKPOINTS.SMALL ? 141 : 68;
}

/**
 * Timeline drag state interface
 */
export interface TimelineDragState {
  isScrollbarDragging: boolean;
  scrollbarDragOffset: number;
  timelineTrackDragProgress: number | null;
}

/**
 * Timeline drag actions
 */
export type TimelineDragAction =
  | { type: 'START_SCROLLBAR_DRAG'; offset?: number }
  | { type: 'UPDATE_SCROLLBAR_DRAG'; offset: number }
  | { type: 'END_SCROLLBAR_DRAG' }
  | { type: 'UPDATE_TIMELINE_TRACK_PROGRESS'; progress: number | null }
  | { type: 'RESET_ALL' };

/**
 * Timeline drag state reducer
 */
export function timelineDragReducer(
  state: TimelineDragState,
  action: TimelineDragAction
): TimelineDragState {
  switch (action.type) {
    case 'START_SCROLLBAR_DRAG':
      return {
        ...state,
        isScrollbarDragging: true,
        scrollbarDragOffset: action.offset || 0,
      };

    case 'UPDATE_SCROLLBAR_DRAG':
      return {
        ...state,
        scrollbarDragOffset: action.offset,
      };

    case 'END_SCROLLBAR_DRAG':
      return {
        ...state,
        isScrollbarDragging: false,
        scrollbarDragOffset: 0,
      };

    case 'UPDATE_TIMELINE_TRACK_PROGRESS':
      return {
        ...state,
        timelineTrackDragProgress: action.progress,
      };

    case 'RESET_ALL':
      return {
        isScrollbarDragging: false,
        scrollbarDragOffset: 0,
        timelineTrackDragProgress: null,
      };

    default:
      return state;
  }
}

/**
 * Shared drag utilities for carousel hooks
 */

export interface DragDelta {
  deltaX: number;
  deltaY: number;
  shouldPreventVerticalScroll: boolean;
}

/**
 * Calculate drag deltas and determine if vertical scroll should be prevented
 */
export function calculateDragDelta(
  clientX: number,
  clientY: number,
  startX: number,
  startY: number,
  sensitivity: number = 1
): DragDelta {
  const deltaX = (clientX - startX) * sensitivity;
  const deltaY = Math.abs(clientY - startY);

  // Prevent vertical scrolling interference
  const shouldPreventVerticalScroll = deltaY > Math.abs(deltaX) && Math.abs(deltaX) < 10;

  return {
    deltaX,
    deltaY,
    shouldPreventVerticalScroll,
  };
}

/**
 * Update velocity calculations for momentum-based dragging
 */
export function updateVelocity(
  dragStateRef: React.MutableRefObject<{
    currentX: number;
    lastTime: number;
    velocity: number;
  }>,
  newOffset: number
): void {
  const now = Date.now();
  const timeDelta = now - dragStateRef.current.lastTime;

  if (timeDelta > 0) {
    dragStateRef.current.velocity = (newOffset - dragStateRef.current.currentX) / timeDelta;
  }

  dragStateRef.current.currentX = newOffset;
  dragStateRef.current.lastTime = now;
}

/**
 * Apply elastic boundary resistance when dragging beyond limits
 */
export function applyElasticBoundary(
  deltaX: number,
  currentIndex: number,
  maxIndex: number,
  containerWidth: number,
  elasticLimit: number = 0.15
): number {
  const elasticLimitPx = containerWidth * elasticLimit;

  // Check if we're at boundaries
  const atStart = currentIndex <= 0;
  const atEnd = currentIndex >= maxIndex;

  if (atStart && deltaX > 0) {
    // Dragging right when at first item - apply elastic resistance
    const elasticDrag = Math.min(deltaX, elasticLimitPx);
    const resistance = 1 - (elasticDrag / elasticLimitPx) * 0.8;
    return elasticDrag * resistance;
  } else if (atEnd && deltaX < 0) {
    // Dragging left when at last item - apply elastic resistance
    const elasticDrag = Math.min(Math.abs(deltaX), elasticLimitPx);
    const resistance = 1 - (elasticDrag / elasticLimitPx) * 0.8;
    return -elasticDrag * resistance;
  }

  return deltaX;
}

/**
 * Calculate momentum boost based on velocity
 */
export function calculateMomentumBoost(
  velocity: number,
  threshold: number = 0.2,
  multiplier: number = 1.5
): number {
  if (Math.abs(velocity) > threshold) {
    return Math.ceil(Math.abs(velocity) * multiplier);
  }
  return 0;
}

/**
 * Check if drag distance meets threshold for navigation
 */
export function checkDragThreshold(
  dragDistance: number,
  itemWidth: number,
  threshold: number
): boolean {
  return Math.abs(dragDistance) >= itemWidth * threshold;
}

/**
 * Mark significant drag for click prevention
 */
export function markDragSignificance(
  offset: number,
  hasDraggedRef: React.MutableRefObject<boolean>,
  threshold: number = 5
): void {
  if (Math.abs(offset) > threshold) {
    hasDraggedRef.current = true;
  }
}

/**
 * Reset drag state and optionally resume auto-advance
 */
export function resetDragState(
  setDragOffset: (offset: number) => void,
  setIsDragging: (isDragging: boolean) => void,
  autoAdvance: boolean,
  playFn: () => void,
  delay: number = 100
): void {
  setDragOffset(0);
  setIsDragging(false);

  if (autoAdvance) {
    setTimeout(() => playFn(), delay);
  }
}

/**
 * Calculate items moved based on drag distance
 */
export function calculateItemsMoved(
  dragDistance: number,
  itemWidth: number,
  minItems: number = 1
): number {
  if (itemWidth === 0) return minItems;
  return Math.max(minItems, Math.round(Math.abs(dragDistance) / itemWidth));
}

/**
 * Calculate target index with momentum and boundaries
 */
export function calculateTargetIndex(
  currentIndex: number,
  direction: number,
  itemsMoved: number,
  velocity: number,
  totalItems: number,
  visibleCards: number,
  momentumEnabled: boolean = false,
  velocityThreshold: number = 0.2,
  momentumMultiplier: number = 1.5
): number {
  let targetIndex = currentIndex + direction * itemsMoved;

  // Apply velocity-based momentum if enabled
  if (momentumEnabled) {
    const momentumBoost = calculateMomentumBoost(velocity, velocityThreshold, momentumMultiplier);
    targetIndex += direction * momentumBoost;
  }

  // Apply boundaries (no looping for drag interactions)
  return Math.max(0, Math.min(targetIndex, totalItems - visibleCards));
}

/**
 * Transform style configuration for carousel positioning
 */
export interface TransformStyleConfig {
  offset: number;
  isDragging: boolean;
  hasTransition?: boolean;
  transition?: string;
}

/**
 * Create transform styles for carousel positioning
 */
export function createTransformStyle(config: TransformStyleConfig): React.CSSProperties {
  const {
    offset,
    isDragging,
    hasTransition = true,
    transition = 'transform 0.35s ease-out',
  } = config;

  const baseStyle: React.CSSProperties = {
    transform: `translateX(${offset}px)`,
  };

  // Only add transition when not dragging and transitions are enabled
  if (!isDragging && hasTransition) {
    baseStyle.transition = transition;
  } else {
    baseStyle.transition = 'none';
  }

  return baseStyle;
}

/**
 * Configuration for container styles
 */
export interface ContainerStyleConfig {
  transformStyle: React.CSSProperties;
  additionalStyles?: React.CSSProperties;
}

/**
 * Create container styles with transform and gap
 */
export function createContainerStyle(config: ContainerStyleConfig): React.CSSProperties {
  const { transformStyle, additionalStyles = {} } = config;

  return {
    ...transformStyle,
    ...additionalStyles,
  };
}

/**
 * Configuration for item styles
 */
export interface ItemStyleConfig {
  itemWidth: number;
  index?: number;
  currentIndex?: number;
  visibleCards?: number;
  isDragging?: boolean;
  additionalStyles?: React.CSSProperties;
}

/**
 * Create item styles for proper sizing and visibility
 */
export function createItemStyle(config: ItemStyleConfig): React.CSSProperties {
  const { itemWidth, additionalStyles = {} } = config;

  return {
    '--carousel-item-width': `${itemWidth}px`,
    ...additionalStyles,
  } as React.CSSProperties;
}

/**
 * Event handler utilities for consolidating mouse and touch event patterns
 */

export interface EventHandlerConfig {
  enableDrag: boolean;
  onDragStart: (event: React.MouseEvent | React.TouchEvent) => void;
  excludeSelector?: string;
  additionalConditions?: () => boolean;
}

/**
 * Create unified mouse down handler with common logic
 */
export function createMouseDownHandler(config: EventHandlerConfig) {
  return (event: React.MouseEvent) => {
    const { enableDrag, onDragStart, excludeSelector, additionalConditions } = config;

    if (!enableDrag) return;

    // Check for excluded elements
    if (excludeSelector) {
      const target = event.target as HTMLElement;
      if (target.closest(excludeSelector)) {
        return;
      }
    }

    // Check additional conditions
    if (additionalConditions && !additionalConditions()) {
      return;
    }

    onDragStart(event);
  };
}

/**
 * Create unified touch start handler with common logic
 */
export function createTouchStartHandler(config: EventHandlerConfig) {
  return (event: React.TouchEvent) => {
    const { enableDrag, onDragStart, excludeSelector, additionalConditions } = config;

    if (!enableDrag) return;

    // Check for excluded elements
    if (excludeSelector) {
      const target = event.target as HTMLElement;
      if (target.closest(excludeSelector)) {
        return;
      }
    }

    // Check additional conditions
    if (additionalConditions && !additionalConditions()) {
      return;
    }

    onDragStart(event);
  };
}

/**
 * Configuration for unified drag event listeners
 */
export interface DragEventListenerConfig {
  isDragging: boolean;
  isMouseDrag: boolean;
  onMouseMove: (event: MouseEvent) => void;
  onMouseUp: (event: MouseEvent) => void;
  onTouchMove: (event: TouchEvent) => void;
  onTouchEnd: (event: TouchEvent) => void;
  preventClickAfterDrag?: (event: Event) => void;
  carouselElement?: HTMLElement | null;
}

/**
 * Create unified drag event listeners with cleanup
 */
export function createDragEventListeners(config: DragEventListenerConfig) {
  const {
    isDragging,
    onMouseMove,
    onMouseUp,
    onTouchMove,
    onTouchEnd,
    preventClickAfterDrag,
    carouselElement,
  } = config;

  // Add global drag listeners when dragging
  if (isDragging) {
    // Mouse events
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    // Touch events
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }

  // Add click prevention listener when not dragging
  if (preventClickAfterDrag && carouselElement) {
    carouselElement.addEventListener('click', preventClickAfterDrag, true);
    return () => {
      carouselElement.removeEventListener('click', preventClickAfterDrag, true);
    };
  }

  return undefined;
}

/**
 * Configuration for auto-advance pause/resume listeners
 */
export interface AutoAdvanceListenerConfig {
  carouselElement: HTMLElement | null;
  autoAdvance: boolean;
  onPause: () => void;
  onResume: () => void;
}

/**
 * Create unified auto-advance pause/resume listeners
 */
export function createAutoAdvanceListeners(config: AutoAdvanceListenerConfig) {
  const { carouselElement, autoAdvance, onPause, onResume } = config;

  if (!carouselElement || !autoAdvance) return undefined;

  const handleMouseEnter = () => onPause();
  const handleMouseLeave = () => onResume();
  const handleFocus = () => onPause();
  const handleBlur = () => onResume();

  carouselElement.addEventListener('mouseenter', handleMouseEnter);
  carouselElement.addEventListener('mouseleave', handleMouseLeave);
  carouselElement.addEventListener('focus', handleFocus, true);
  carouselElement.addEventListener('blur', handleBlur, true);

  return () => {
    carouselElement.removeEventListener('mouseenter', handleMouseEnter);
    carouselElement.removeEventListener('mouseleave', handleMouseLeave);
    carouselElement.removeEventListener('focus', handleFocus, true);
    carouselElement.removeEventListener('blur', handleBlur, true);
  };
}
